// Zone selection helpers shared by the Calendar, Weekly Calendar and Map screens.
//
// A selected zone id is persisted (sessionStorage) so the screens keep the user's
// zone across reloads and navigation. That persisted id can go stale at any time:
// an admin can unassign the zone from the employee or remove it altogether, and
// the browser session still holds the old id. Sending a stale zone id to
// Calendar_api / MapGrid_api / MapCanvas_api produces a "zone not assigned"
// error instead of a usable screen.
//
// Rule enforced here: a persisted zone id is only usable when it is still present
// in the latest ZoneList_api response. Otherwise fall back to the company default
// zone (DefaultId === 1), then to the first zone in the list.

export const CALENDAR_ZONE_SESSION_KEY = "selectedZoneIDInCalendarScreen";

const asZoneArray = (zones) => (Array.isArray(zones) ? zones : []);

// ZoneList_api shape: { Data: { Records: { ZoneList: [...] } } }
export const getZoneListFromResponse = (zoneResponse) =>
  asZoneArray(zoneResponse?.Data?.Records?.ZoneList);

export const getDefaultZoneFromList = (zones = []) => {
  const list = asZoneArray(zones);
  return list.find((zone) => Number(zone?.DefaultId) === 1) || list[0] || null;
};

export const findZoneInList = (zones = [], zoneId) => {
  const targetId = Number(zoneId);
  if (!targetId) return null;

  return asZoneArray(zones).find((zone) => Number(zone?.Id) === targetId) || null;
};

// The zone a screen should actually use: the requested (persisted) zone when it is
// still in ZoneList, otherwise the default zone. Returns null for an empty list.
export const resolveZoneFromList = (zones = [], requestedZoneId = null) =>
  findZoneInList(zones, requestedZoneId) || getDefaultZoneFromList(zones);

// Single writer for the persisted calendar zone id, so a zone id that is missing
// from ZoneList can never be stored. Returns the zone that was persisted, or null
// when nothing could be resolved.
// `zones` empty means "no ZoneList to validate against yet" — the caller's id is
// kept as-is and validated on the next ZoneList_api response, rather than the
// selection being dropped.
export const persistCalendarZoneId = (zoneId, zones = []) => {
  const list = asZoneArray(zones);
  const requestedId = Number(zoneId) || 0;
  const resolvedZone = list.length > 0 ? resolveZoneFromList(list, requestedId) : null;
  const idToPersist = Number(resolvedZone?.Id ?? requestedId) || 0;

  try {
    if (!idToPersist) {
      sessionStorage.removeItem(CALENDAR_ZONE_SESSION_KEY);
      return null;
    }

    sessionStorage.setItem(CALENDAR_ZONE_SESSION_KEY, String(idToPersist));
  } catch (error) {
    console.error("Error persisting selected zone id:", error);
  }

  return resolvedZone;
};

export const getPersistedZoneId = () => {
  try {
    return Number(sessionStorage.getItem(CALENDAR_ZONE_SESSION_KEY)) || null;
  } catch (error) {
    console.error("Error reading selected zone id:", error);
    return null;
  }
};
